import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, catchError, filter, from, Observable, switchMap, take, throwError } from 'rxjs';
import { CommonService } from './common.service';
import { JwtVerificationService } from './jwt-verification.service';
import { environment } from '../../../environments/environment';
import { AuthGuardService } from '../guard/auth-guard/auth-guard.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const commonService = inject(CommonService);
  const router = inject(Router);
  const spinner = inject(NgxSpinnerService);
  const jwtVerificationService = inject(JwtVerificationService);
  const authSrv = inject(AuthGuardService);

  // Skip the token endpoint itself (no bearer token attached to that call) and any request
  // that doesn't carry one - nothing to verify.
  const bearerToken = req.headers.get('IdToken') || req.headers.get('Authorization');
  if (!bearerToken || req.url.includes(environment.tokenURL)) {
    return handleRequest(req, next, commonService, router, spinner, authSrv);
  }

  // SECURITY (REVERTED 2026-09-30 - see jwt-verification.service.ts's top-of-file incident
  // note): per-request JWT verification here is DETECTION/LOGGING ONLY again - it must NEVER
  // be the thing that blocks or redirects a request. Blocking was briefly re-enabled
  // (AppSec Findings #3/#7), but that immediately combined with two compounding risks: (1)
  // a jose/JWKS concurrency race that can intermittently make ONE of many truly-concurrent
  // calls verifying the SAME valid token come back 'invalid' (this exact risk was already
  // documented from TWO prior incidents - see jwt-verification.md v5/v7 - before blocking was
  // re-enabled the third time), and (2) an aud/iss format assumption that turned out to be
  // wrong for real production tokens (now fixed to fail-open, see JwtVerificationService).
  // Together these caused a real login/reload loop. Every API call on a page fires through
  // here concurrently with the SAME token (e.g. Home alone fires 5+ simultaneous calls on
  // load), making this the highest-risk place to hit either issue. A forged/tampered token
  // is still authoritatively caught at the two low-frequency checkpoints in app.component.ts
  // (right after login, and once per app bootstrap) - those are the only places allowed to
  // redirect to /unauthorized over a signature failure. Do NOT re-enable blocking here again
  // without first eliminating the underlying JWKS concurrency race at its root (has defeated
  // multiple dedicated fix attempts already) AND confirming the real aud/iss claim format
  // against live production tokens - and get explicit, informed user sign-off given this
  // history (asked once already and got no direct answer before the last attempt).
  return from(jwtVerificationService.verifyToken(bearerToken, `${req.method} ${req.url}`)).pipe(
    switchMap(result => {
      if (result.status === 'invalid') {
        console.warn(`[auth.interceptor] JWT failed verification for ${req.method} ${req.url} (not blocking - detection/logging only, see incident note above).`);
      }
      return handleRequest(req, next, commonService, router, spinner, authSrv);
    })
  );
};




function handleRequest(req: HttpRequest<any>, next: HttpHandlerFn, commonService: CommonService, router: Router, spinner: NgxSpinnerService, authSrv: AuthGuardService) {
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip the token endpoint itself to avoid infinite loops
      if (req.url.includes(environment.tokenURL)) {
        return throwError(() => error);
      }

      // Anything other than a 401 is not something this interceptor needs to handle.
      // Note: 404 is intentionally NOT handled globally here — many endpoints legitimately
      // return 404 to mean "no data found" rather than "this page/route doesn't exist", so
      // navigation-on-404 is handled locally by the specific components that load a resource
      // by id (e.g. a bookmarked disclosure), not for every API call in the app.
      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        // Another refresh is in progress — queue this request until it completes
        return refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(newToken => next(cloneWithToken(req, newToken!)).pipe(
            catchError(retryError => handleRetryFailure(retryError, spinner, router))
          ))
        );
      }

      isRefreshing = true;
      refreshTokenSubject.next(null);

      return commonService.refreshTokenForInterceptor().pipe(
        switchMap((newToken: string | null) => {
          isRefreshing = false;

          if (newToken) {
            refreshTokenSubject.next(newToken);
            return next(cloneWithToken(req, newToken)).pipe(
              catchError(retryError => handleRetryFailure(retryError, spinner, router))
            );
          }

          // Refresh failed — session truly expired, redirect to login
          redirectToLogin(authSrv);
          return throwError(() => error);
        }),
        catchError(err => {
          isRefreshing = false;
          redirectToLogin(authSrv);
          return throwError(() => err);
        })
      );
    })
  );
}

// Handles the response of a request that was retried with a freshly refreshed token.
// If it still comes back as 401, the token itself is fine — the signed-in user simply no
// longer has access to this resource (e.g. their role/permission was revoked), so we show
// the Unauthorized page instead of looping on the refresh flow.
function handleRetryFailure(error: HttpErrorResponse, spinner: NgxSpinnerService, router: Router): Observable<never> {
  if (error.status === 401) {
    spinner.hide();
    router.navigate(['/unauthorized']);
  }
  return throwError(() => error);
}

function cloneWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      Authorization: token,
      IdToken: token
    }
  });
}

function redirectToLogin(authSrv: AuthGuardService): void {
  setTimeout(() => {
    sessionStorage.clear();
    localStorage.clear();
    authSrv.clearTokens();
    window.location.href = environment.redirectURI;
  }, 500);
}

